import React, { useEffect, useState } from "react";
import { Button } from "reactstrap";
import {
  engagementId,
  engagementValue,
  engagementQuickFilters,
} from "./engagementSelectionData";

// Select engagements before assigning the review team.
export default function EngagementSelection({
  onCreate,
  createdEngagementId,
  population,
  selectedIds,
  lockedIds,
  onChange,
  onBack,
  onSave,
  onDraft,
  busy,
  loading,
}) {
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [workType, setWorkType] = useState("");
  const [partner, setPartner] = useState("");
  const [quickFilters, setQuickFilters] = useState([]);
  const [confirmed, setConfirmed] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  useEffect(() => {
    setConfirmed(false);
    setPage(1);
  }, [selectedIds]);

  useEffect(() => {
    if (!createdEngagementId) return;
    setTab("all");
    setSearch("");
    setWorkType("");
    setPartner("");
    setQuickFilters([]);
    setPage(1);
  }, [createdEngagementId]);

  const name = (item) =>
    item.name || item.engagement?.name || "Untitled engagement";
  const selected = population.filter((item) =>
    selectedIds.includes(engagementId(item))
  );
  const notSelected = population.filter(
    (item) => !selectedIds.includes(engagementId(item))
  );
  const visible = (
    tab === "all" ? population : tab === "selected" ? selected : notSelected
  ).filter((item) =>
    `${name(item)} ${
      item.client?.clientName || item.client?.name || item.clientName || ""
    } ${engagementValue(item, "partner")} ${engagementValue(item, "type")}`
      .toLowerCase()
      .includes(search.toLowerCase())
  );
  const filtered = visible.filter(
    (item) =>
      (!workType || engagementValue(item, "type") === workType) &&
      (!partner || engagementValue(item, "partner") === partner) &&
      engagementQuickFilters.every(
        (filter) => !quickFilters.includes(filter.id) || filter.matches(item)
      )
  );
  const effectivePageSize =
    pageSize === "all" ? Math.max(filtered.length, 1) : pageSize;
  const pageItems = filtered.slice(
    (page - 1) * effectivePageSize,
    page * effectivePageSize
  );

  return (
    <section className="engagement-selection">
      <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
        <h2>
          All engagements{" "}
          <small>
            {population.length} total &middot; {selected.length} selected
          </small>
        </h2>
        {onCreate && (
          <Button color="primary" disabled={busy || loading} onClick={onCreate}>
            <i className="bx bx-plus" aria-hidden="true" /> New engagement
          </Button>
        )}
      </div>
      <p>Select engagements to include in this inspection cycle.</p>
      <div
        className="engagement-selection-quick-filters"
        role="group"
        aria-label="Quick filters"
      >
        <strong>Quick filters</strong>
        <div>
          {engagementQuickFilters.map((filter) => (
            <button
              key={filter.id}
              type="button"
              aria-pressed={quickFilters.includes(filter.id)}
              onClick={() => {
                setQuickFilters((current) =>
                  current.includes(filter.id)
                    ? current.filter((id) => id !== filter.id)
                    : [...current, filter.id]
                );
                setPage(1);
              }}
            >
              {filter.label}
            </button>
          ))}
          <button
            type="button"
            className="engagement-selection-quick-filters__clear"
            disabled={
              !quickFilters.length &&
              !search &&
              !workType &&
              !partner &&
              tab === "all"
            }
            onClick={() => {
              setQuickFilters([]);
              setSearch("");
              setWorkType("");
              setPartner("");
              setTab("all");
              setPage(1);
            }}
          >
            Clear filters
          </button>
        </div>
      </div>
      <div className="engagement-selection-tabs">
        <select
          aria-label="Selection status"
          value={tab}
          onChange={(event) => {
            setTab(event.target.value);
            setPage(1);
          }}
        >
          <option value="all">All engagements ({population.length})</option>
          <option value="selected">Selected ({selected.length})</option>
          <option value="available">Not selected ({notSelected.length})</option>
        </select>
        <input
          type="search"
          aria-label="Search engagements"
          placeholder="Search engagements by name, partner, or type"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
        />
        {[
          ["Work type", "type", workType, setWorkType],
          ["Partner", "partner", partner, setPartner],
        ].map(([label, field, value, setter]) => (
          <select
            key={field}
            aria-label={label}
            value={value}
            onChange={(event) => {
              setter(event.target.value);
              setPage(1);
            }}
          >
            <option value="">{label}</option>
            {[
              ...new Set(
                population
                  .map((item) => engagementValue(item, field))
                  .filter(Boolean)
              ),
            ]
              .sort()
              .map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
          </select>
        ))}
      </div>
      {loading ? (
        <p>Loading engagements...</p>
      ) : (
        <div className="engagement-selection-table-wrap">
          <table>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    aria-label="Check visible engagements"
                    disabled={busy}
                    checked={
                      pageItems.length > 0 &&
                      pageItems.every((item) =>
                        selectedIds.includes(engagementId(item))
                      )
                    }
                    onChange={(event) => {
                      const ids = pageItems
                        .map(engagementId)
                        .filter((id) => !lockedIds.includes(id));
                      onChange(
                        event.target.checked
                          ? [...new Set([...selectedIds, ...ids])]
                          : selectedIds.filter((id) => !ids.includes(id)),
                        ids,
                        "Manually selected"
                      );
                      setConfirmed(false);
                    }}
                  />
                </th>
                <th>Engagement</th>
                <th>Client / Firm</th>
                <th>Work Type</th>
                <th>Industry Type</th>
                <th>Partner</th>
                <th>Risk</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((item) => {
                const id = engagementId(item);
                const locked = lockedIds.includes(id);
                return (
                  <React.Fragment key={id}>
                    <tr>
                      <td>
                        <input
                          type="checkbox"
                          disabled={locked || busy}
                          aria-label={`${
                            tab === "selected" ? "Remove" : "Add"
                          } ${name(item)}`}
                          checked={selectedIds.includes(id)}
                          onChange={() => {
                            onChange(
                              selectedIds.includes(id)
                                ? selectedIds.filter((value) => value !== id)
                                : [...selectedIds, id],
                              [id],
                              "Manually selected"
                            );
                            setConfirmed(false);
                          }}
                        />
                      </td>
                      <td>
                        {name(item)}
                        <small>{item.engagementNo || ""}</small>
                        {locked && <small>Review already generated</small>}
                      </td>
                      <td>
                        {item.client?.clientName ||
                          item.client?.name ||
                          item.clientName ||
                          "Unknown"}
                      </td>
                      <td>{engagementValue(item, "type") || "Unknown"}</td>
                      <td>
                        {item.industryType?.name ||
                          item.industry?.name ||
                          "Unknown"}
                      </td>
                      <td>{engagementValue(item, "partner") || "Unknown"}</td>
                      <td>{engagementValue(item, "risk") || "Unknown"}</td>
                    </tr>
                  </React.Fragment>
                );
              })}
              {!pageItems.length && (
                <tr>
                  <td colSpan="7">No engagements in this view.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <div className="engagement-selection-pagination">
        <span>
          Showing {filtered.length ? (page - 1) * effectivePageSize + 1 : 0}–
          {Math.min(page * effectivePageSize, filtered.length)} of{" "}
          {filtered.length}
        </span>
        <label className="engagement-selection-page-size">
          Records per page
          <select
            aria-label="Records per page"
            value={pageSize}
            onChange={(event) => {
              setPageSize(
                event.target.value === "all"
                  ? "all"
                  : Number(event.target.value)
              );
              setPage(1);
            }}
          >
            <option value="all">All</option>
            {[10, 25, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
        <Button outline disabled={page <= 1} onClick={() => setPage(page - 1)}>
          Previous
        </Button>
        <Button
          outline
          disabled={page * effectivePageSize >= filtered.length}
          onClick={() => setPage(page + 1)}
        >
          Next
        </Button>
      </div>
      <label className="engagement-selection-confirm">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(event) => setConfirmed(event.target.checked)}
        />{" "}
        I have verified the selected engagements.
      </label>
      <footer className="engagement-selection-actions">
        <Button outline disabled={busy} onClick={onBack}>
          Back to Inspection Details
        </Button>
        <Button outline disabled={busy} onClick={onDraft}>
          Save draft
        </Button>
        <Button
          color="primary"
          disabled={busy || loading || !confirmed || !selected.length}
          onClick={onSave}
        >
          Continue to Team Assignment
        </Button>
      </footer>
    </section>
  );
}
